import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

const baseCls = (className) => cn(
  'flex h-10 w-full rounded-md border border-input bg-surface px-3 py-1 text-[14px] text-foreground transition-[border-color,box-shadow] duration-fast hover:border-border-strong',
  // The placeholder sits a shade under the typed value so the two are never
  // mistaken for each other, now that muted ink is darker.
  'placeholder:text-muted-foreground',
  'file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground',
  'focus-visible:outline-none focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/10',
  'disabled:cursor-not-allowed disabled:opacity-50',
  className
);

// Every password input gets a show/hide (eye) toggle for free, so users can
// check what they typed on login / signup / superadmin / any secret field.
// Pass `noToggle` when a wrapper (e.g. SecretField) renders its own toggle.
const Input = React.forwardRef(({ className, type, noToggle, ...props }, ref) => {
  const [show, setShow] = React.useState(false);
  if (type !== 'password' || noToggle) {
    return <input type={type} className={baseCls(className)} ref={ref} {...props} />;
  }
  return (
    <div className="relative w-full">
      <input type={show ? 'text' : 'password'} className={baseCls(cn('pr-9', className))} ref={ref} {...props} />
      <button
        type="button"
        tabIndex={-1}
        aria-label={show ? 'Hide password' : 'Show password'}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setShow((v) => !v)}
        className="absolute inset-y-0 right-0 grid w-9 place-items-center text-muted-foreground transition-colors hover:text-foreground"
      >
        {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
});
Input.displayName = 'Input';

export { Input };
